<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';

    const apiBase = getApiBase();
    let groupedRequests = {};
    let loading = true;
    let showAll = false;

    // map of requestId → boolean (expanded?)
    let expandedMap = {};

    // Computed values for separated requests
    // All requests are ClassRequest instances (ClassResponsibility instances are linked via has_responsibility_incident flag)
    $: pendingRequests = filterRequestsByStatus(groupedRequests, 'Pending');
    $: otherRequests = filterRequestsByStatus(groupedRequests, ['Accepted', 'Denied']);

    async function handleFetchRequests() {
      const token = localStorage.getItem('jwtToken');
      try {
        const res = await fetch(
          `${apiBase}/studentplayground/api/requests/admin-reschedule/`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type':  'application/json'
            }
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        groupedRequests = await res.json();
      } catch (err) {
        console.error('Error fetching requests:', err);
        alert(`Error loading requests: ${err.message}`);
      } finally {
        loading = false;
      }
    }

    onMount(handleFetchRequests);

    // Function to filter requests by status
    function filterRequestsByStatus(grouped, statusFilter) {
      const filtered = {};
      const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
      
      for (const [day, requests] of Object.entries(grouped)) {
        const filteredRequests = requests.filter(req => statuses.includes(req.status));
        if (filteredRequests.length > 0) {
          filtered[day] = filteredRequests;
        }
      }
      
      return filtered;
    }

    // toggle expanded state for one card
    function toggleExpand(id) {
      expandedMap[id] = !expandedMap[id];
      // reassign to trigger Svelte reactivity
      expandedMap = { ...expandedMap };
    }

    function formatDateTime(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      return `${d.toLocaleDateString()} at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }

    function getStudentName(student) {
      return student || 'Unknown Student';
    }

    function getTeacherName(teacher) {
      return teacher || 'Unknown Teacher';
    }

    function requestSubject(request) {
      if (!request.class_id) {
        return 'Ištrinta pamoka';
      }
      return request.subject || 'Nežinomas dalykas';
    }

    // Function to handle admin confirm/deny actions
    async function handleAdminAction(requestId, action) {
      const token = localStorage.getItem('jwtToken');
      
      try {
        const response = await fetch(
          `${apiBase}/studentplayground/api/requests/admin-reschedule-action/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              request_id: requestId,
              action: action
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          const actionText = action === 'confirm' ? 'confirmed' : action === 'deny' ? 'denied' : 'marked as handled';
          alert(`Request ${actionText} successfully!`);
          // Refresh the requests list
          await handleFetchRequests();
        } else {
          const error = await response.json();
          console.error('Failed to perform action:', error);
          alert(`Failed to ${action} request: ${error.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error in admin action:', error);
        alert(`Error ${action}ing request: ${error.message}`);
      }
    }
  </script>
  
  <style>
  /* Header styling matching AdminCreateUserComponent */
  .labelDiv {
    display: flex;
    justify-content: space-around;
    margin-bottom: 2rem;
  }
  .labelDiv span {
    font-size: 1.2rem;
    font-weight: bold;
    color: #333;
  }

  /* Loading and empty states */
  .loading-message, .empty-message {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
  }

  /* Date group styling */
  .date-group {
    margin-bottom: 2rem;
  }
  .date-header {
    font-size: 1.1rem;
    font-weight: bold;
    color: #0055aa;
    border-bottom: 2px solid #0055aa;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  /* Request card styling */
  .request-card {
    border: 1px solid #ccc;
    border-radius: 8px;
    margin-bottom: 1rem;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
  }
  .request-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  /* Responsibility incident styling - red background */
  .request-card.responsibility-incident {
    background: #ffe6e6;
    border-color: #ffcccc;
  }
  .request-card.responsibility-incident:hover {
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.2);
  }

  /* Summary button styling */
  .request-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1rem;
    border: none;
    background: #fff;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;
    font-size: 1rem;
  }
  .request-summary:hover {
    background-color: #f8f9fa;
  }

  /* Status-based coloring */
  .request-summary.pending {
    border-left: 4px solid #ffc107;
  }
  .request-summary.accepted {
    border-left: 4px solid #28a745;
  }
  .request-summary.denied {
    border-left: 4px solid #dc3545;
  }

  /* Summary content layout */
  .summary-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    margin-right: 1rem;
  }
  .summary-main {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .student-name {
    font-weight: bold;
    color: #333;
    font-size: 1rem;
  }
  .teacher-name {
    color: #666;
    font-size: 0.9rem;
  }
  .agreed-upon-badge {
    color: #28a745;
    font-size: 0.8rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .request-type-badge {
    font-size: 0.8rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .cancel-badge {
    color: #dc3545;
  }
  .same-day-cancel-badge {
    color: #8b0000;
    font-weight: bold;
  }

  /* Status badge */
  .status-badge {
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .status-pending {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }
  .status-accepted {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  .status-denied {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  /* Expand icon */
  .expand-icon {
    color: #0055aa;
    font-size: 0.8rem;
    transition: transform 0.2s ease;
  }

  /* Details section */
  .request-details {
    padding: 1.5rem;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
  }
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .detail-item.full-width {
    grid-column: 1 / -1;
  }
  .detail-label {
    font-weight: bold;
    color: #0055aa;
    font-size: 0.9rem;
  }
  .detail-value {
    color: #333;
    font-size: 1rem;
    padding: 0.4rem;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  .reason-text {
    min-height: 3rem;
    resize: none;
    font-family: inherit;
  }

  /* Admin action buttons */
  .admin-actions {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
  }
  
  .action-button {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .confirm-button {
    background-color: #28a745;
    color: white;
  }
  
  .confirm-button:hover {
    background-color: #218838;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }
  
  .deny-button {
    background-color: #dc3545;
    color: white;
  }
  
  .deny-button:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }
  
  .action-button:active {
    transform: translateY(0);
  }

  /* Show All button styling */
  .show-all-container {
    text-align: center;
    margin: 2rem 0;
    padding: 1rem;
  }
  
  .show-all-button {
    padding: 0.8rem 2rem;
    background-color: #0055aa;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1rem;
    box-shadow: 0 2px 4px rgba(0, 85, 170, 0.2);
  }
  
  .show-all-button:hover {
    background-color: #004494;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 85, 170, 0.3);
  }
  
  .show-all-button:active {
    transform: translateY(0);
  }

  /* Section headers */
  .section-header {
    font-size: 1.3rem;
    font-weight: bold;
    color: #0055aa;
    margin: 2rem 0 1rem 0;
    padding: 0.5rem 0;
    border-bottom: 3px solid #0055aa;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .details-grid {
      grid-template-columns: 1fr;
    }
    .summary-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    .status-badge {
      align-self: flex-end;
    }
    .admin-actions {
      flex-direction: column;
      gap: 0.5rem;
    }
    .action-button {
      justify-content: center;
    }
  }
</style>
  
  <div class="labelDiv">
  <span>RESCHEDULE REQUESTS</span>
</div>

{#if loading}
  <div class="loading-message">Loading requests…</div>
{:else if Object.keys(groupedRequests).length === 0}
  <div class="empty-message">No reschedule requests found.</div>
{:else}
  <!-- Pending Requests Section -->
  {#if Object.keys(pendingRequests).length > 0}
    <h1 class="section-header">Pending Requests</h1>
    {#each Object.entries(pendingRequests) as [day, requests]}
      <div class="date-group">
        <h2 class="date-header">{day}</h2>

        {#each requests as req (req.id)}
          <div class="request-card" class:responsibility-incident={req.has_responsibility_incident}>
            <!-- Summary Header -->
            <button
              type="button"
              class="request-summary"
              on:click={() => toggleExpand(req.id)}
              class:pending={req.status === 'Pending'}
              class:accepted={req.status === 'Accepted'}
              class:denied={req.status === 'Denied'}
            >
              <div class="summary-content">
                <div class="summary-main">
                  <span class="student-name">{getStudentName(req.student)}</span>
                  <span class="teacher-name">with {getTeacherName(req.teacher)}</span>
                  {#if req.has_responsibility_incident && req.request_type === 'Cancel'}
                    <span class="request-type-badge cancel-badge same-day-cancel-badge">ATSAUKIMAS TA PACIA DIENA</span>
                  {:else if req.request_type === 'Cancel'}
                    <span class="request-type-badge cancel-badge">ATŠAUKIMAS</span>
                  {/if}
                  {#if !req.has_responsibility_incident}
                    {#if req.agreed_upon}
                      <span class="agreed-upon-badge">ABU SUTARĖ</span>
                    {:else if req.additional_class}
                      <span class="agreed-upon-badge">PAPILDOMA PAMOKA</span>
                    {/if}
                  {/if}
                </div>
                <div class="status-badge" class:status-pending={req.status === 'Pending'} 
                     class:status-accepted={req.status === 'Accepted'} 
                     class:status-denied={req.status === 'Denied'}>
                  {req.status}
                </div>
              </div>
              <div class="expand-icon">
                {expandedMap[req.id] ? '▼' : '▶'}
              </div>
            </button>

            <!-- Expanded Details -->
            {#if expandedMap[req.id]}
              <div class="request-details">
                <div class="details-grid">
                  <div class="detail-item">
                    <span class="detail-label">Request ID:</span>
                    <span class="detail-value">#{req.id}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Request Type:</span>
                    <span class="detail-value">
                      {req.request_type || 'Reschedule'}
                      {#if req.has_responsibility_incident && req.incident_type}
                        {' (' + req.incident_type + ' - Same Day)'}
                      {/if}
                    </span>
                  </div>
                  {#if req.has_responsibility_incident}
                    <div class="detail-item">
                      <span class="detail-label">Incident Type:</span>
                      <span class="detail-value">{req.incident_type || 'N/A'}</span>
                    </div>
                  {/if}
                  <div class="detail-item">
                    <span class="detail-label">Student:</span>
                    <span class="detail-value">{getStudentName(req.student)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Subject:</span>
                    <span class="detail-value">{requestSubject(req)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Teacher:</span>
                    <span class="detail-value">{getTeacherName(req.teacher)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Current Time:</span>
                    <span class="detail-value">{formatDateTime(req.old_time)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Requested Time:</span>
                    <span class="detail-value">{req.request_type === 'Cancel' ? 'N/A (Cancellation)' : formatDateTime(req.new_requested_time)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Directed to:</span>
                    <span class="detail-value">{req.directed_to || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Agreed Upon:</span>
                    <span class="detail-value">{req.agreed_upon ? 'Yes' : 'No'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Additional Class:</span>
                    <span class="detail-value">{req.additional_class ? 'Yes' : 'No'}</span>
                  </div>
                  <div class="detail-item full-width">
                    <span class="detail-label">Reason:</span>
                    <span class="detail-value reason-text">{req.reason || 'No reason provided'}</span>
                  </div>
                  {#if req.comment}
                    <div class="detail-item full-width">
                      <span class="detail-label">Comment:</span>
                      <span class="detail-value reason-text">{req.comment}</span>
                    </div>
                  {/if}
                  
                  <!-- Admin action buttons for pending requests -->
                  {#if req.status === 'Pending' && !req.has_responsibility_incident}
                    <div class="detail-item full-width">
                      <span class="detail-label">Admin Actions:</span>
                      <div class="admin-actions">
                        <button 
                          class="action-button confirm-button"
                          on:click={() => handleAdminAction(req.id, 'confirm')}
                        >
                          ✓ Confirm Request
                        </button>
                        <button 
                          class="action-button deny-button"
                          on:click={() => handleAdminAction(req.id, 'deny')}
                        >
                          ✗ Deny Request
                        </button>
                      </div>
                    </div>
                  {/if}
                  
                  <!-- Mark as Handled button for requests with responsibility incidents -->
                  {#if req.has_responsibility_incident && req.status === 'Pending'}
                    <div class="detail-item full-width">
                      <span class="detail-label">Admin Actions:</span>
                      <div class="admin-actions">
                        <button 
                          class="action-button confirm-button"
                          on:click={() => handleAdminAction(req.id, 'mark_handled')}
                        >
                          ✓ Mark as Handled
                        </button>
                      </div>
                    </div>
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  {:else}
    <div class="empty-message">No pending reschedule requests found.</div>
  {/if}

  <!-- Show All Button -->
  {#if Object.keys(otherRequests).length > 0}
    <div class="show-all-container">
      <button 
        class="show-all-button"
        on:click={() => showAll = !showAll}
      >
        {showAll ? 'Hide Processed Requests' : 'Show All Requests'}
      </button>
    </div>
  {/if}

  <!-- Processed Requests Section (shown when showAll is true) -->
  {#if showAll && Object.keys(otherRequests).length > 0}
    <h1 class="section-header">Processed Requests</h1>
    {#each Object.entries(otherRequests) as [day, requests]}
      <div class="date-group">
        <h2 class="date-header">{day}</h2>

        {#each requests as req (req.id)}
          <div class="request-card" class:responsibility-incident={req.has_responsibility_incident}>
            <!-- Summary Header -->
            <button
              type="button"
              class="request-summary"
              on:click={() => toggleExpand(req.id)}
              class:pending={req.status === 'Pending'}
              class:accepted={req.status === 'Accepted'}
              class:denied={req.status === 'Denied'}
            >
              <div class="summary-content">
                <div class="summary-main">
                  <span class="student-name">{getStudentName(req.student)}</span>
                  <span class="teacher-name">with {getTeacherName(req.teacher)}</span>
                  {#if req.has_responsibility_incident && req.request_type === 'Cancel'}
                    <span class="request-type-badge cancel-badge same-day-cancel-badge">ATSAUKIMAS TA PACIA DIENA</span>
                  {:else if req.request_type === 'Cancel'}
                    <span class="request-type-badge cancel-badge">ATŠAUKIMAS</span>
                  {/if}
                  {#if !req.has_responsibility_incident}
                    {#if req.agreed_upon}
                      <span class="agreed-upon-badge">ABU SUTARĖ</span>
                    {:else if req.additional_class}
                      <span class="agreed-upon-badge">PAPILDOMA PAMOKA</span>
                    {/if}
                  {/if}
                </div>
                <div class="status-badge" class:status-pending={req.status === 'Pending'} 
                     class:status-accepted={req.status === 'Accepted'} 
                     class:status-denied={req.status === 'Denied'}>
                  {req.status}
                </div>
              </div>
              <div class="expand-icon">
                {expandedMap[req.id] ? '▼' : '▶'}
              </div>
            </button>

            <!-- Expanded Details -->
            {#if expandedMap[req.id]}
              <div class="request-details">
                <div class="details-grid">
                  <div class="detail-item">
                    <span class="detail-label">Request ID:</span>
                    <span class="detail-value">#{req.id}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Request Type:</span>
                    <span class="detail-value">
                      {req.request_type || 'Reschedule'}
                      {#if req.has_responsibility_incident && req.incident_type}
                        {' (' + req.incident_type + ' - Same Day)'}
                      {/if}
                    </span>
                  </div>
                  {#if req.has_responsibility_incident}
                    <div class="detail-item">
                      <span class="detail-label">Incident Type:</span>
                      <span class="detail-value">{req.incident_type || 'N/A'}</span>
                    </div>
                  {/if}
                  <div class="detail-item">
                    <span class="detail-label">Student:</span>
                    <span class="detail-value">{getStudentName(req.student)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Subject:</span>
                    <span class="detail-value">{requestSubject(req)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Teacher:</span>
                    <span class="detail-value">{getTeacherName(req.teacher)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Current Time:</span>
                    <span class="detail-value">{formatDateTime(req.old_time)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Requested Time:</span>
                    <span class="detail-value">{req.request_type === 'Cancel' ? 'N/A (Cancellation)' : formatDateTime(req.new_requested_time)}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Directed to:</span>
                    <span class="detail-value">{req.directed_to || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Agreed Upon:</span>
                    <span class="detail-value">{req.agreed_upon ? 'Yes' : 'No'}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Additional Class:</span>
                    <span class="detail-value">{req.additional_class ? 'Yes' : 'No'}</span>
                  </div>
                  <div class="detail-item full-width">
                    <span class="detail-label">Reason:</span>
                    <span class="detail-value reason-text">{req.reason || 'No reason provided'}</span>
                  </div>
                  {#if req.comment}
                    <div class="detail-item full-width">
                      <span class="detail-label">Comment:</span>
                      <span class="detail-value reason-text">{req.comment}</span>
                    </div>
                  {/if}
                  
                  <!-- Mark as Handled button for requests with responsibility incidents -->
                  {#if req.has_responsibility_incident && req.status === 'Pending'}
                    <div class="detail-item full-width">
                      <span class="detail-label">Admin Actions:</span>
                      <div class="admin-actions">
                        <button 
                          class="action-button confirm-button"
                          on:click={() => handleAdminAction(req.id, 'mark_handled')}
                        >
                          ✓ Mark as Handled
                        </button>
                      </div>
                    </div>
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/each}
  {/if}
{/if}
